import { Injectable, inject } from '@angular/core';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { Store, Action } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { map, catchError, switchMap, withLatestFrom, filter, tap, delay } from 'rxjs/operators';
import { scrollToElement, HttpErrorMessage } from '@aston/foundation';
import { IDunningActionCompleteFormPayload, IDunningActionForm } from 'apps/middle/src/app/dunnings-module/models';
import { DunningActionsSpecsService, DunningActionsService } from 'apps/middle/src/app/dunnings-module/services';
import { WebSocketService } from 'apps/middle/src/app/shared-module/services';
import { WsEventType } from 'apps/middle/src/app/shared-module/enums';
import { TranslateService } from '@ngx-translate/core';

import { DunningTimelineStore } from '../../../dunnings-module/stores/dunning-timeline/dunning-timeline.store';
import { DunningActionRealizedStore } from '../../../dunnings-module/stores/dunning-action-realized';
import { SharedTemplatesStore } from '../../../shared-module/stores/shared-templates.store';
import { SuperDebtorPageStore } from '../../../debtors-module/stores/super-debtor-page';
import { CorrelationParams, aggregate } from '../../models/correlated-actions.model';
import * as AppStoreSelectors from '../../app-store/selectors';
import * as AppStoreActions from '../../app-store/actions';
import * as featureSelectors from '../selectors';
import * as featureActions from '../actions';

@Injectable({
	providedIn: 'root'
})
export class DunningActionDetailStoreCompletionEffects {

	private tlStore = inject(DunningTimelineStore);
	private sdPageStore = inject(SuperDebtorPageStore);
	private translateService = inject(TranslateService);
	private dunningActionsService = inject(DunningActionsService);
	private dasService = inject(DunningActionsSpecsService);
	private darStore = inject(DunningActionRealizedStore);
	private templatesStore = inject(SharedTemplatesStore)
	private actions$ = inject(Actions);
	private store = inject(Store);
	private ws = inject(WebSocketService);

	initCompletionFormRequests$: Observable<Action> = createEffect(() => this.actions$.pipe(
		ofType(featureActions.InitCompletionFormRequest),
		withLatestFrom(this.store.select(featureSelectors.selectState)),
		switchMap(([action, state]) => {
			const correlationParams: CorrelationParams = action.correlationParams;
			correlationParams.parentActionType = action.type;

			return [
				// handle action
				featureActions.LoadDunningActionRequest(action.actionId, correlationParams),

				// other panel data request actions

				// wont reload templates if already present
				AppStoreActions.Execute(() => this.templatesStore.loadAllTemplates()),

				// always reload contacts
				featureActions.LoadContactsRequest(correlationParams),

				// dont reload users if already present
				state.assignableUsers.entity.length > 0 ?
					featureActions.LoadAssignableUsersSuccess({
						entity: state.assignableUsers.entity, correlationParams
					}) :
					featureActions.LoadAssignableUsersRequest(correlationParams),

				// dont reload users if already present
				state.superAdminUsers.entity.length > 0 ?
					featureActions.LoadSuperAdminUsersSuccess({
						entity: state.superAdminUsers.entity, correlationParams
					}) :
					featureActions.LoadSuperAdminUsersRequest(correlationParams),

				// dont reload users if already present
				state.allUsers.entity.length > 0 ?
					featureActions.LoadAllUsersSuccess({entity: state.allUsers.entity, correlationParams}) :
					featureActions.LoadAllUsersRequest(correlationParams),

				// dont reload clips if already present
				state.clips.entity.length > 0 && (state.superDebtorId === action.superDebtorId) ?
					featureActions.LoadClipsSuccess({entity: state.clips.entity, correlationParams}) :
					featureActions.LoadClipsRequest(correlationParams),

				// accounting
				featureActions.LoadCompletionAccountingRequest(action.actionId, correlationParams),

				// handle comments
				featureActions.LoadCommentsRequest(action.actionId, correlationParams),
			];
		}),
		catchError(error => of(featureActions.InitCompletionFormFailure({error})))
	));

	initCompletionFormSuccess$: Observable<Action> = createEffect(() => this.actions$.pipe(
		ofType(featureActions.InitCompletionFormRequest),
		// Create an aggregated action listener
		// it will observe the race of all specified observable
		// and complete when all these actions are dispatched.
		// Note that individual actions workflow still apply.
		aggregate(
			this.actions$.pipe(ofType(featureActions.LoadDunningActionSuccess)),
			// other panel data success actions types
			// if you change something here, make sure the next pipe's arguments are correct !
			this.actions$.pipe(ofType(featureActions.LoadAssignableUsersSuccess)),
			this.actions$.pipe(ofType(featureActions.LoadAllUsersSuccess)),
			this.actions$.pipe(ofType(featureActions.LoadCommentsSuccess)),
			this.actions$.pipe(ofType(featureActions.LoadCompletionAccountingSuccess)),
			this.actions$.pipe(ofType(featureActions.InitCompletionFormFailure)),
		),
		switchMap(([
			// please keep these for clarity
			dunningActionSuccess,
			assignableUsersSuccess, // eslint-disable-line
			usersSuccess, // eslint-disable-line
			commentsSuccess, // eslint-disable-line
			accountingSuccess // eslint-disable-line
		]) => {
			const templates = this.templatesStore.templatesLight()
			const dAction = dunningActionSuccess.entity as IDunningActionForm;

			// This form should not open if the action is already realized
			if (dAction.realizationDate) return [
				featureActions.CancelCompletionForm(),
				featureActions.CloseForm(),
			]

			const preselectedTemplate = dAction.dunningTemplateId ?
				templates.find(template => template.id === dAction.dunningTemplateId) :
				null;

			return this.dasService.getSpecsOnce().pipe(
				switchMap(specs => [
					// here we can initialize the whole popin form
					// with every loaded components
					featureActions.InitCompletionFormSuccess(
						dAction as IDunningActionForm,
						dunningActionSuccess.correlationParams),

					featureActions.UpdateCompletionForm({param: specs[dAction.type].mapToCompletionForm(dAction)}),

					// refresh selected accounting documents
					featureActions.LoadSelectedAccountingRequest(),

					// handle attachments
					featureActions.LoadAttachmentsRequest(dAction.id, dunningActionSuccess.correlationParams),

					// load selected template
					preselectedTemplate ?
						featureActions.LoadTemplateRequest({param: preselectedTemplate}) :
						AppStoreActions.Noop()
				]));
		}),
		catchError(error => of(AppStoreActions.ErrorLog(error), featureActions.InitCompletionFormFailure(error)))
	));

	initCompletionFormScroll$: Observable<any> = createEffect(() => this.actions$.pipe(
		ofType(featureActions.InitCompletionFormSuccess),
		// TODO: find a better trigger to know when everything is loaded
		delay(500),
		map(_ => scrollToElement('#super-debtor-dunning-completion-anchor'))
	), {dispatch: false});

	loadCompletionAccountingRequestEffect$: Observable<Action> = createEffect(() => this.actions$.pipe(
		ofType(featureActions.LoadCompletionAccountingRequest),
		switchMap(action =>
			this.dunningActionsService.getActionAccounting(action.id).pipe(
				map(list => featureActions.LoadCompletionAccountingSuccess({
					entity: list, correlationParams: action.correlationParams
				})),
				catchError(error => of(featureActions.LoadCompletionAccountingFailure({error})))
			)
		)
	));

	completeActionRequestEffect$: Observable<Action> = createEffect(() => this.actions$.pipe(
		ofType(featureActions.CompleteDunningActionRequest),
		withLatestFrom(this.store.select(AppStoreSelectors.selectCurrentUser)),
		switchMap(([action, user]) => this.dunningActionsService.completeAction(action.entity).pipe(
			map(_ => featureActions.CompleteDunningActionSuccess({entity: action.entity})),
			catchError((error: HttpErrorMessage) => {
				if (error.is('InvalidAssignedOperator') && user.isSupport) {
					error = error.resetKey('CannotCompleteAsSupport').translate(this.translateService)
				}
				return of(featureActions.CompleteDunningActionFailure({error}))
			})
		))
	));

	afterCompleteActionRequestEffect$: Observable<Action> = createEffect(() => this.actions$.pipe(
		ofType(featureActions.CompleteDunningActionSuccess),
		withLatestFrom(this.dasService.getSpecsStream()),
		delay(1000),
		switchMap(([action, specs]) => {
			const openDocument = specs[action.entity.dunningActionType].openDocumentUponCompletion;
			return [
				openDocument ? AppStoreActions.Execute(() => this.darStore.quickView(action.entity.id)) : AppStoreActions.Noop(),
				AppStoreActions.ToastSuccess('Dunning.Actions.Messages.Completed'),
				AppStoreActions.Execute(() => {
					this.sdPageStore.loadDunningCases();
					this.tlStore.loadTimeline();
				}),
			]
		})
	));

	submitFormToCompletionEffect$: Observable<Action> = createEffect(() => this.actions$.pipe(
		ofType(featureActions.SubmitFormCompleteDunning),
		withLatestFrom(this.store.select(featureSelectors.selectIsCompletionMode)),
		filter(([_, isCompletionMode]) => isCompletionMode),
		tap(_ => this.ws.emitLocally(WsEventType.RefreshBoardTasks)),
		withLatestFrom(this.dasService.getSpecsStream()),
		map(([[{entity}], specs]) => [
			entity,
			specs[entity.dunningActionType].needsSendingCostsCheck,
		]),
		map(([entity, needsCostsCheck]: [IDunningActionCompleteFormPayload, boolean, boolean])  =>
			needsCostsCheck ?
				featureActions.CheckSendingCostsBalanceRequest(entity) :
				featureActions.CompleteDunningActionRequest(entity))
	));

	cancelCompletionFormConfirm$: Observable<Action> = createEffect(() => this.actions$.pipe(
		ofType(featureActions.CancelCompletionFormConfirm),
		map(_ => AppStoreActions.OpenConfirmationModal({
			textsKey: `Dunning.Confirmations.CancelCompletionForm.`
		}, featureActions.CancelCompletionForm())
		)
	));
}
